/**
 * PlaybookBrowser - Read-only browser for FAIR Remediation Playbooks
 *
 * Fetches playbook entries from the ontology improvement API and renders
 * them grouped by FAIR category. Each playbook is an expandable card showing
 * diagnosis, remediation steps with code snippets, effort estimate, and
 * regulatory relevance.
 *
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState, useEffect } from 'react';
import BrowserLayout from './BrowserLayout';
import { useApiClient } from '../../hooks/useApiClient';

// ============================================================================
// TYPES
// ============================================================================

interface RemediationStep {
  step_number: number;
  title: string;
  description: string;
  code_language?: string;
  code_snippet?: string;
}

interface PlaybookEntry {
  indicator_id: string;
  indicator_name: string;
  category: string;
  severity: string;
  diagnosis: string;
  remediation_steps: RemediationStep[];
  effort_estimate: string;
  regulatory_relevance?: string;
}

interface ApiResponse {
  playbooks: PlaybookEntry[];
}

// ============================================================================
// PAGE CONSTANTS
// ============================================================================

const PAGE_TITLE = 'FAIR Remediation Playbooks';
const PAGE_DESCRIPTION =
  'Step-by-step fix guides for common FAIR and structural ontology gaps. Each playbook includes diagnosis, remediation steps with code examples, effort estimates, and regulatory relevance.';

/**
 * Canonical category ordering and display labels.
 */
const CATEGORY_ORDER: { key: string; label: string }[] = [
  { key: 'findable', label: 'Findable (F)' },
  { key: 'accessible', label: 'Accessible (A)' },
  { key: 'interoperable', label: 'Interoperable (I)' },
  { key: 'reusable', label: 'Reusable (R)' },
  { key: 'structural', label: 'Structural Ontology Issues' },
];

// ============================================================================
// BADGE HELPERS
// ============================================================================

/**
 * Maps a severity value to the appropriate badge CSS class.
 */
function severityBadgeClass(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'badge badge-red';
    case 'warning':
      return 'badge badge-amber';
    case 'info':
      return 'badge badge-blue';
    default:
      return 'badge badge-gray';
  }
}

/**
 * Maps an effort_estimate value to the appropriate badge CSS class.
 */
function effortBadgeClass(effort: string): string {
  switch (effort.toLowerCase()) {
    case 'quick_win':
      return 'badge badge-green';
    case 'moderate':
      return 'badge badge-amber';
    case 'significant':
      return 'badge badge-red';
    default:
      return 'badge badge-gray';
  }
}

/**
 * Formats an effort_estimate key into a human-readable label.
 */
function effortLabel(effort: string): string {
  switch (effort.toLowerCase()) {
    case 'quick_win':
      return 'Quick Win';
    case 'moderate':
      return 'Moderate';
    case 'significant':
      return 'Significant';
    default:
      return effort;
  }
}

/**
 * Maps severity to the playbook-indicator dot CSS modifier.
 */
function indicatorDotClass(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'playbook-indicator critical';
    case 'warning':
      return 'playbook-indicator high';
    case 'info':
      return 'playbook-indicator medium';
    default:
      return 'playbook-indicator low';
  }
}

// ============================================================================
// PLAYBOOK CARD COMPONENT
// ============================================================================

interface PlaybookCardProps {
  entry: PlaybookEntry;
}

const PlaybookCard: React.FC<PlaybookCardProps> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="template-card playbook-card">
      {/* Collapsed header — always visible */}
      <button
        type="button"
        className="playbook-card-header"
        style={{ background: 'none', border: 'none', padding: 0, width: '100%', textAlign: 'left', cursor: 'pointer' }}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span
          className={indicatorDotClass(entry.severity)}
          aria-hidden="true"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
            <span
              style={{
                fontWeight: 700,
                color: '#2563eb',
                fontSize: '0.8125rem',
                fontFamily: 'SF Mono, Fira Code, Cascadia Code, Consolas, monospace',
                flexShrink: 0,
              }}
            >
              {entry.indicator_id}
            </span>
            <div className="template-card-badges" style={{ margin: 0, gap: '0.25rem' }}>
              <span className={severityBadgeClass(entry.severity)}>
                {entry.severity.charAt(0).toUpperCase() + entry.severity.slice(1).toLowerCase()}
              </span>
              <span className={effortBadgeClass(entry.effort_estimate)}>
                {effortLabel(entry.effort_estimate)}
              </span>
            </div>
          </div>
          <h3 style={{ margin: 0, fontSize: '0.9375rem', color: '#1e293b', lineHeight: 1.35 }}>
            {entry.indicator_name}
          </h3>
        </div>
        <span
          aria-hidden="true"
          style={{
            color: '#94a3b8',
            fontSize: '0.75rem',
            marginLeft: '0.5rem',
            flexShrink: 0,
            alignSelf: 'center',
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div style={{ marginTop: '0.875rem' }}>
          {/* Diagnosis */}
          <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8125rem', color: '#334155', lineHeight: 1.6 }}>
            {entry.diagnosis}
          </p>

          {/* Regulatory relevance */}
          {entry.regulatory_relevance && (
            <p
              style={{
                margin: '0 0 0.75rem 0',
                fontSize: '0.75rem',
                color: '#64748b',
                lineHeight: 1.5,
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '0.375rem',
                padding: '0.5rem 0.75rem',
              }}
            >
              <strong style={{ color: '#475569' }}>Regulatory Relevance: </strong>
              {entry.regulatory_relevance}
            </p>
          )}

          {/* Remediation steps */}
          {entry.remediation_steps && entry.remediation_steps.length > 0 && (
            <ol className="playbook-steps">
              {entry.remediation_steps.map((step) => (
                <li key={step.step_number}>
                  <strong style={{ display: 'block', marginBottom: '0.2rem', color: '#1e293b' }}>
                    {step.title}
                  </strong>
                  <span>{step.description}</span>
                  {step.code_snippet && (
                    <pre className="playbook-code">{step.code_snippet}</pre>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PLAYBOOKBROWSER PAGE
// ============================================================================

const PlaybookBrowser: React.FC = () => {
  const { authFetch } = useApiClient();
  const [playbooks, setPlaybooks] = useState<PlaybookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPlaybooks = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch('/api/ontology-improvement/playbooks');

        if (!response.ok) {
          throw new Error(`Failed to load playbooks (HTTP ${response.status})`);
        }

        const data: ApiResponse = await response.json() as ApiResponse;

        if (!cancelled) {
          setPlaybooks(data.playbooks ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPlaybooks();

    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  /**
   * Groups playbooks by category following CATEGORY_ORDER.
   * Playbooks whose category does not appear in the order list are appended last.
   */
  const groupedPlaybooks = (): Array<{ key: string; label: string; entries: PlaybookEntry[] }> => {
    const knownKeys = new Set(CATEGORY_ORDER.map((c) => c.key));

    // Build a map: category key → entries
    const map = new Map<string, PlaybookEntry[]>();
    for (const entry of playbooks) {
      const key = entry.category.toLowerCase();
      const bucket = map.get(key) ?? [];
      bucket.push(entry);
      map.set(key, bucket);
    }

    const groups: Array<{ key: string; label: string; entries: PlaybookEntry[] }> = [];

    // Add in canonical order first
    for (const { key, label } of CATEGORY_ORDER) {
      const entries = map.get(key);
      if (entries && entries.length > 0) {
        groups.push({ key, label, entries });
      }
    }

    // Append any unknown categories at the end
    for (const [key, entries] of map.entries()) {
      if (!knownKeys.has(key) && entries.length > 0) {
        groups.push({
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1),
          entries,
        });
      }
    }

    return groups;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="browser-loading">
          <div className="spinner" />
          Loading playbooks...
        </div>
      );
    }

    if (error) {
      return (
        <div className="browser-error">
          <div className="error-icon">!</div>
          <p>{error}</p>
        </div>
      );
    }

    if (playbooks.length === 0) {
      return (
        <div className="browser-loading">
          <p>No playbooks found.</p>
        </div>
      );
    }

    const groups = groupedPlaybooks();

    return (
      <>
        {groups.map(({ key, label, entries }) => (
          <div key={key} className="playbook-category-group">
            <h2 className="playbook-category-title">{label}</h2>
            <div className="template-card-grid">
              {entries.map((entry) => (
                <PlaybookCard key={entry.indicator_id} entry={entry} />
              ))}
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <BrowserLayout title={PAGE_TITLE} description={PAGE_DESCRIPTION}>
      {renderContent()}
    </BrowserLayout>
  );
};

export default PlaybookBrowser;
